import type { FileCategory } from '@yawi/types';

interface CompressionConfig {
  maxWidth: number;
  maxHeight: number;
  quality: number;
}

const SKIP_THRESHOLD_BYTES = 100 * 1024; // 100KB — no comprimir si ya es pequeño

const CONFIG: Partial<Record<FileCategory, CompressionConfig>> = {
  avatars: { maxWidth: 800, maxHeight: 800, quality: 0.85 },
  pets: { maxWidth: 1200, maxHeight: 1200, quality: 0.85 },
  vehicles: { maxWidth: 1200, maxHeight: 1200, quality: 0.85 },
  amenities: { maxWidth: 1920, maxHeight: 1080, quality: 0.88 },
  tickets: { maxWidth: 1600, maxHeight: 1200, quality: 0.85 },
  announcements: { maxWidth: 1600, maxHeight: 1200, quality: 0.85 },
  payments: { maxWidth: 2048, maxHeight: 2048, quality: 0.85 },
  general: { maxWidth: 2048, maxHeight: 2048, quality: 0.85 },
  // documents: sin compresión — pueden ser PDFs, Excel, etc.
};

function calcDimensions(
  srcWidth: number,
  srcHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  if (srcWidth <= maxWidth && srcHeight <= maxHeight) {
    return { width: srcWidth, height: srcHeight };
  }
  const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
  return { width: Math.round(srcWidth * ratio), height: Math.round(srcHeight * ratio) };
}

async function resizeAndCompress(
  file: File,
  config: CompressionConfig
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const { width, height } = calcDimensions(
        img.naturalWidth,
        img.naturalHeight,
        config.maxWidth,
        config.maxHeight
      );

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file);
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Intentar WebP, fallback a JPEG
      const tryBlob = (mimeType: string, onFail?: () => void) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              if (onFail) onFail();
              else resolve(file);
              return;
            }
            const ext = mimeType === 'image/webp' ? '.webp' : '.jpg';
            const baseName = file.name.replace(/\.[^.]+$/, '');
            resolve(new File([blob], `${baseName}${ext}`, { type: mimeType }));
          },
          mimeType,
          config.quality
        );
      };

      tryBlob('image/webp', () => tryBlob('image/jpeg'));
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image for compression'));
    };

    img.src = objectUrl;
  });
}

/**
 * Comprime y redimensiona una imagen antes de subirla.
 * Retorna el File original si no es imagen, ya es pequeño, o la categoría no requiere compresión.
 */
export async function compressImageForUpload(
  file: File,
  category: FileCategory
): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  if (file.size < SKIP_THRESHOLD_BYTES) return file;

  const config = CONFIG[category];
  if (!config) return file;

  try {
    return await resizeAndCompress(file, config);
  } catch {
    return file;
  }
}
